import { Product, ProductTenor } from '@shared/protos/product';
import { ColDef } from 'ag-grid-enterprise';
import FlashingCellRenderer from '../shared/ag-grid/FlashingCellRenderer';
import { getBalDisplayName } from '../shared/utils';
import { BidAskType, PricesRow } from './types';

const TENOR_COL_ID = 'tenorId';

export const tenorColumn: ColDef<PricesRow> = {
  field: 'tenorName',
  colId: TENOR_COL_ID,
  headerName: '',
  suppressSpanHeaderHeight: true,
  minWidth: 80,
  maxWidth: 120,
  lockPosition: 'left',
  pinned: 'left',
  cellClass: 'ag-tenor-column',
  resizable: true,
};

const createPriceCols = (
  symbol: string,
  isMobile: boolean,
  colIndex: number,
  type: 'bid' | 'mid' | 'ask',
  selectedBidAskColumns: BidAskType[],
  columnWidths: Record<string, number>,
  hideMidColumn: boolean
) => {
  const baseColDef = {
    minWidth: !isMobile ? 50 : 90,
    cellClassRules: {
      'ag-col-group-even': () => colIndex % 2 === 0,
    },
  };

  const config = {
    bid: { headerName: 'Bid', valueField: 'buy', bidAskType: BidAskType.Bid },
    mid: { headerName: 'Mid', valueField: 'mid' },
    ask: { headerName: 'Ask', valueField: 'sell', bidAskType: BidAskType.Ask },
  };
  const { bidAskType, valueField, headerName } = config[type] as { bidAskType?: BidAskType; valueField: string; headerName: string };

  const colId = `${symbol}-${type}`;

  return {
    ...baseColDef,
    colId,
    width: columnWidths[colId] ?? 80,
    headerName: headerName,
    cellRenderer: type !== 'mid' ? FlashingCellRenderer : undefined,
    valueGetter: (params: any) => params.data?.[symbol]?.[valueField],
    valueSetter: (params: any) => {
      if (!params.data[symbol]) {
        params.data[symbol] = { buy: null, mid: null, sell: null };
        return false;
      }
      params.data[symbol][valueField] = params.newValue;
      return true;
    },
    cellClass: (params: any) => {
      return type !== 'mid' && params.data?.[symbol]?.[valueField] !== null && params.data?.[symbol]?.[valueField] !== '' ? 'teal-cell' : '';
    },
    cellClassRules: {
      'ag-col-group-even': () => colIndex % 2 === 0,
    },
    hide: type === 'mid' ? hideMidColumn : bidAskType !== undefined ? !selectedBidAskColumns.includes(bidAskType) : false,
  };
};

export const mapColumns = (
  products: Product[],
  selectedColumns: string[],
  columnsOrder: string[],
  isMobile: boolean,
  selectedBidAskColumns: BidAskType[],
  isShowCurrencyUnit: boolean,
  columnWidths: Record<string, number>,
  hideMidColumn: boolean = false
) => {
  const columns = products
    .sort((a, b) => columnsOrder.indexOf(`${a.symbol}-mid`) - columnsOrder.indexOf(`${b.symbol}-mid`))
    .reduce<ColDef<any, any>[]>(
      (accumulator, { symbol, short_name, full_name, currency_unit }, colIndex) => {
        const priceCols = ['bid', 'mid', 'ask'].map(type =>
          createPriceCols(symbol, isMobile, colIndex, type as 'bid' | 'mid' | 'ask', selectedBidAskColumns, columnWidths, hideMidColumn)
        );
        return [
          ...accumulator,
          {
            colId: symbol,
            headerName: short_name,
            headerTooltip: full_name,
            hide: !selectedColumns.includes(symbol),
            resizable: !isMobile,
            marryChildren: true,
            children: isShowCurrencyUnit
              ? [
                  {
                    colId: currency_unit,
                    headerName: currency_unit,
                    resizable: !isMobile,
                    marryChildren: true,
                    suppressHeaderContextMenu: true,
                    children: priceCols,
                  },
                ]
              : priceCols,
          },
        ];
      },
      [tenorColumn]
    );

  return columns;
};

export const mapRows = (tenors: ProductTenor[], products: Product[], firstWeekIndex: number, firstMonthIndex: number): PricesRow[] => {
  return tenors.map(({ code, display, frequency }, index) => {
    const newRow = {
      tenorId: code,
      tenorName: getBalDisplayName(firstMonthIndex, firstWeekIndex, index, display),
      tenorFrequency: frequency,
    };
    products.forEach(({ symbol, product_type }) => {
      newRow[symbol] = { mid: '', buy: '', sell: '' };
    });

    return newRow as PricesRow;
  });
};
